/**
 * Genera interpretación de afinidad al puesto para RRHH.
 * Compara resultados del candidato con el benchmark del rol.
 */

import { getCommercialName } from '@/config/batteries';
import { getRoleTypeLabel } from '@/config/tests';
import type { TestBenchmark, DimensionBenchmark } from '@/types';

const MINI_IPIP_DIM_NAMES: Record<string, string> = {
  responsabilidadOrden: 'Responsabilidad y Orden',
  estabilidadEmocional: 'Estabilidad Emocional',
  energiaSocial: 'Energía Social',
  cooperacion: 'Cooperación',
  aperturaAdaptabilidad: 'Apertura y Adaptabilidad',
};

const IMPORTANCE_LABELS: Record<number, string> = {
  3: 'Muy alta',
  2: 'Alta',
  1: 'Complementaria',
};

/** Niveles MINI_IPIP (Bajo, Promedio, Alto) mapean a vocabulario benchmark (En desarrollo, Adecuado, Alto). */
export function normalizeMiniIpipLevelForBenchmark(level: string | undefined): string | undefined {
  if (!level) return undefined;
  const map: Record<string, string> = {
    Bajo: 'En desarrollo',
    Promedio: 'Adecuado',
    Alto: 'Alto',
  };
  return map[level] ?? level;
}

export type AffinityMatch = 'yes' | 'exceeds' | 'below' | 'no';

export interface AffinityItem {
  name: string;
  importance: string;
  expected: string;
  candidate: string;
  match: AffinityMatch;
  weight: number;
}

/** Etiqueta para RRHH: evita confusión entre "Supera" (positivo) y "Por debajo" (revisar). */
export function getMatchLabel(match: AffinityMatch): string {
  return match === 'yes' ? 'Sí' : match === 'exceeds' ? 'Supera' : match === 'below' ? 'Por debajo' : 'No';
}

export interface AffinityInterpretation {
  roleLabel: string;
  affinityPercent: number;
  items: AffinityItem[];
  summaryText: string;
}

export interface ComputeAffinityInput {
  jobProfile: Record<string, TestBenchmark>;
  scores: Array<{ testId: string; scorePercent?: number; scaleScore?: number }>;
  moduleAggregates: Record<string, unknown>;
  roleType?: string | null;
}

/**
 * Calcula afinidad y genera interpretación.
 * Replica la lógica de ResultsPage para el porcentaje y añade items + resumen.
 */
export function computeAffinityInterpretation(input: ComputeAffinityInput): AffinityInterpretation | null {
  const { jobProfile, scores, moduleAggregates, roleType } = input;
  if (!jobProfile || Object.keys(jobProfile).length === 0) return null;

  const roleLabel = roleType ? getRoleTypeLabel(roleType) : 'Puesto';

  let totalWeight = 0;
  let weightedSum = 0;
  const items: AffinityItem[] = [];

  // ─── Pruebas (JK_OFFICE_ADMIN, APO_6, RA_10, etc.) ───
  for (const [testId, bench] of Object.entries(jobProfile)) {
    if (testId === 'MINI_IPIP_20') continue;
    if (!bench.weight || bench.weight <= 0) continue;

    const score = scores.find((s) => s.testId === testId);
    const pct = score?.scorePercent ?? (typeof score?.scaleScore === 'number' ? score.scaleScore : undefined);

    if (bench.minScore === undefined) continue;

    const displayName =
      testId === 'RA_10' ? getCommercialName('estilo_trabajo_organizacion') : getCommercialName(testId);
    const importance = IMPORTANCE_LABELS[bench.weight] ?? 'Complementaria';

    let match: AffinityMatch = 'no';
    let candidateLabel = '—';

    if (typeof pct === 'number') {
      if (pct >= bench.minScore) {
        match = 'yes';
        candidateLabel = `${pct}%`;
      } else {
        const diff = bench.minScore - pct;
        match = diff <= 15 ? 'below' : 'no';
        candidateLabel = `${pct}%`;
      }
    }

    let matchVal = 100;
    if (match === 'below') matchVal = Math.max(0, 100 - (bench.minScore - (pct ?? 0)));
    else if (match === 'no') matchVal = Math.max(0, 100 - (bench.minScore - (pct ?? 0)));
    weightedSum += matchVal * bench.weight;
    totalWeight += bench.weight;

    items.push({
      name: displayName,
      importance,
      expected: `≥${bench.minScore}%`,
      candidate: candidateLabel,
      match,
      weight: bench.weight,
    });
  }

  // ─── MINI_IPIP_20 dimensiones ───
  const ipipBench = jobProfile['MINI_IPIP_20'];
  const ipipData = moduleAggregates.MINI_IPIP_20 as { dimensions?: Record<string, { level?: string }> } | undefined;

  if (ipipBench?.dimensions && ipipData?.dimensions) {
    for (const [dimKey, dimConf] of Object.entries(ipipBench.dimensions)) {
      if (!dimConf || dimConf.weight <= 0 || dimConf.expectedLevel === 'Indiferente') continue;

      const candLevel = ipipData.dimensions?.[dimKey]?.level;
      const candNorm = normalizeMiniIpipLevelForBenchmark(candLevel);
      const expectedLevel = dimConf.expectedLevel as string;

      let dimMatch = 0;
      let match: AffinityMatch = 'no';
      if (candNorm === expectedLevel) {
        dimMatch = 100;
        match = 'yes';
      } else if (
        (expectedLevel === 'En desarrollo' && candNorm === 'Adecuado') ||
        (expectedLevel === 'Adecuado' && candNorm === 'Alto')
      ) {
        dimMatch = 50;
        match = 'exceeds';
      } else if (
        (expectedLevel === 'Alto' && candNorm === 'Adecuado') ||
        (expectedLevel === 'Adecuado' && candNorm === 'En desarrollo')
      ) {
        dimMatch = 50;
        match = 'below';
      }

      weightedSum += dimMatch * dimConf.weight;
      totalWeight += dimConf.weight;
      const candidateDisplay = candLevel ?? '—';

      items.push({
        name: MINI_IPIP_DIM_NAMES[dimKey] ?? dimKey,
        importance: IMPORTANCE_LABELS[dimConf.weight] ?? 'Complementaria',
        expected: expectedLevel,
        candidate: candidateDisplay,
        match,
        weight: dimConf.weight,
      });
    }
  }

  const affinityPercent = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;

  // Ordenar por peso (desc)
  items.sort((a, b) => b.weight - a.weight);

  const summaryText = buildSummaryText(affinityPercent, items, roleLabel);

  return {
    roleLabel,
    affinityPercent,
    items,
    summaryText,
  };
}

function buildSummaryText(affinityPercent: number, items: AffinityItem[], roleLabel: string): string {
  const matched = items.filter((i) => i.match === 'yes');
  const exceeds = items.filter((i) => i.match === 'exceeds');
  const mismatched = items.filter((i) => i.match === 'no' || i.match === 'below');
  const highWeightMismatch = mismatched.filter((i) => i.weight >= 2);

  if (mismatched.length === 0) {
    return `El candidato presenta un perfil muy alineado con el puesto de ${roleLabel}.`;
  }

  const affinityLabel =
    affinityPercent >= 80 ? 'alta' : affinityPercent >= 60 ? 'media' : 'baja';

  let mainReason = '';
  if (highWeightMismatch.length > 0) {
    const first = highWeightMismatch[0];
    mainReason = `${first.name} en nivel ${first.candidate}, cuando el puesto prioriza ${first.expected}`;
  } else if (mismatched.length > 0) {
    const first = mismatched[0];
    mainReason = `${first.name} en nivel ${first.candidate}, cuando el puesto prioriza ${first.expected}`;
  }

  const matchStr =
    matched.length > 0
      ? `Coincide en ${matched.map((i) => i.name).slice(0, 3).join(', ')}${matched.length > 3 ? ' y otros' : ''}.`
      : '';
  const exceedsStr =
    exceeds.length > 0
      ? ` Supera en ${exceeds.map((i) => i.name).slice(0, 2).join(', ')}${exceeds.length > 2 ? ' y otros' : ''}.`
      : '';
  const strengths = matchStr + exceedsStr;

  if (mainReason && strengths) {
    return `${strengths} La afinidad ${affinityLabel} (${affinityPercent}%) se debe principalmente a ${mainReason}. Conviene revisar en entrevista según el puesto.`;
  }
  if (mainReason) {
    return `La afinidad ${affinityLabel} (${affinityPercent}%) se debe principalmente a ${mainReason}. Conviene revisar en entrevista según el puesto.`;
  }

  return `Afinidad ${affinityLabel} (${affinityPercent}%) al perfil de ${roleLabel}. Interpretar junto con entrevista y requisitos del puesto.`;
}
